import React, { useMemo, useState, createContext, useContext } from 'react';
import { MenuItem, calculateCartTotal } from '../data/menu';
export interface CartItem {
  item: MenuItem;
  quantity: number;
}
interface OrderContextType {
  pickupDate: Date | null;
  setPickupDate: (date: Date | null) => void;
  cart: CartItem[];
  addToCart: (item: MenuItem, quantity: number) => void;
  removeFromCart: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  cartTotal: number;
  cartCount: number;
  notes: string;
  setNotes: (notes: string) => void;
  firstName: string;
  setFirstName: (name: string) => void;
  lastName: string;
  setLastName: (name: string) => void;
  phone: string;
  setPhone: (phone: string) => void;
}
const OrderContext = createContext<OrderContextType | undefined>(undefined);
export const OrderProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const [pickupDate, setPickupDate] = useState<Date | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [notes, setNotes] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const addToCart = (item: MenuItem, quantity: number) => {
    setCart(prev => {
      const existing = prev.find(ci => ci.item.id === item.id);
      if (existing) {
        return prev.map(ci => ci.item.id === item.id ? {
          ...ci,
          quantity: ci.quantity + quantity
        } : ci);
      }
      return [...prev, {
        item,
        quantity
      }];
    });
  };
  const removeFromCart = (itemId: string) => {
    setCart(prev => prev.filter(ci => ci.item.id !== itemId));
  };
  const updateQuantity = (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(itemId);
      return;
    }
    setCart(prev => prev.map(ci => ci.item.id === itemId ? {
      ...ci,
      quantity
    } : ci));
  };
  const clearCart = () => {
    setCart([]);
    setNotes('');
    setFirstName('');
    setLastName('');
    setPhone('');
    setPickupDate(null);
  };
  const cartTotal = useMemo(() => calculateCartTotal(cart), [cart]);
  const cartCount = useMemo(() => {
    return cart.reduce((count, ci) => count + ci.quantity, 0);
  }, [cart]);
  return <OrderContext.Provider value={{
    pickupDate,
    setPickupDate,
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    cartTotal,
    cartCount,
    notes,
    setNotes,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    phone,
    setPhone
  }} data-id="element-54">
      {children}
    </OrderContext.Provider>;
};
export const useOrder = () => {
  const context = useContext(OrderContext);
  if (context === undefined) {
    throw new Error('useOrder must be used within an OrderProvider');
  }
  return context;
};